import React, { FC, useRef } from "react";
import { notification, Table } from "antd";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  useConfirmDeleteModal,
  useDebouncedTableFilter,
  useGetUsersColumns,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import { handleRequestError } from "shared/helpers";
import {
  UserItem,
  useDeleteUserMutation,
  useGetUsersQuery,
  useSetNewPasswordMutation,
} from "shared/api";
import { useChangePasswordModal } from "shared/hooks/useChangePasswordModal";
import { RenderClearFiltersButton } from "shared/ui";

interface UsersTableProps {
  showEditUserModal: () => void;
  changeSort: (ascendOrdering: string, descendOrdering: string) => void;
}

const UsersTable: FC<UsersTableProps> = ({ showEditUserModal, changeSort }) => {
  const { ordering } = useTableSorting();
  const pagination = usePagination(20);
  const { setCurrentPage, setPageSize, pageSize, currentPage, offset, limit } =
    pagination;

  const { setValues } = useFormikContext<UserItem>();
  const { t } = useTranslation();

  const editUserHandler = (record: UserItem) => {
    showEditUserModal();
    setValues({
      first_name: record.first_name,
      last_name: record.last_name,
      username: record.username,
      email: record.email,
      id: record.id,
      roles: record.roles,
      is_superuser: record.is_superuser,
    });
  };

  const [api, contextHolder] = notification.useNotification();

  const [deleteUser] = useDeleteUserMutation();
  const selectedUserIdRef = useRef<number | null>(null);
  const [setNewPassword, { isLoading }] = useSetNewPasswordMutation();

  const {
    debouncedValue: debouncedUserNameValue,
    handleFilterChange: handleUserNameValue,
    value: userNameValue,
    clearFilterValue: clearUserNameValue,
  } = useDebouncedTableFilter("username", 400);

  const {
    debouncedValue: debouncedFirstNameValue,
    handleFilterChange: handleFirstNameChange,
    value: firstNameValue,
    clearFilterValue: clearFirstNameValue,
  } = useDebouncedTableFilter("first_name", 400);

  const {
    debouncedValue: debouncedLastNameValue,
    handleFilterChange: handleLastNameChange,
    value: lastNameValue,
    clearFilterValue: clearLastNameValue,
  } = useDebouncedTableFilter("last_name", 400);

  const {
    debouncedValue: debouncedEmailValue,
    handleFilterChange: handleEmailChange,
    value: emailValue,
    clearFilterValue: clearEmailValue,
  } = useDebouncedTableFilter("email", 400);

  const {
    debouncedValue: debouncedSuperuserValue,
    handleFilterChange: handleSuperuserChange,
    value: superuserValue,
    clearFilterValue: clearSuperuserValue,
  } = useDebouncedTableFilter("is_superuser", 400);

  const {
    debouncedValue: debouncedRolesValue,
    handleFilterChange: handleRolesChange,
    value: rolesValue,
    clearFilterValue: clearRolesValue,
  } = useDebouncedTableFilter("roles", 400);

  const {
    data: allUsers,
    isFetching: isLoadingUsersFetching,
    isLoading: isLoadingAllUsers,
    refetch,
  } = useGetUsersQuery({
    ordering,
    offset,
    limit,
    expand: "roles",
    username__icontains: debouncedUserNameValue,
    first_name__icontains: debouncedFirstNameValue,
    last_name__icontains: debouncedLastNameValue,
    email__icontains: debouncedEmailValue,
    is_superuser: debouncedSuperuserValue,
    roles__name__icontains: debouncedRolesValue,
  });

  const deleteUserHandler = async (record: UserItem) => {
    try {
      await deleteUser(record).unwrap();
      api.success({
        message: t("users-page.success-title"),
        description: t("users-page.success-delete-desc"),
      });
      refetch();
    } catch (error) {
      const description = handleRequestError(
        error,
        t("users-page.error-title")
      );
      api.error({
        message: t("users-page.error-title"),
        description,
      });
    }
  };

  const showDeleteUserModal = useConfirmDeleteModal(
    t("modal-placeholders.delete-warning-message"),
    deleteUserHandler
  );

  const changePasswordModal = useChangePasswordModal(
    t("Set a new password"),
    async (newPassword) => {
      const userId = selectedUserIdRef.current;
      if (!userId) return;
      await setNewPassword({ userId, newPassword }).unwrap();
      api.success({
        message: t("The password was successfully set"),
      });
    },
    isLoading
  );

  const clearAllFilters = () => {
    clearUserNameValue();
    clearFirstNameValue();
    clearLastNameValue();
    clearEmailValue();
    clearRolesValue();
    clearSuperuserValue();
  };

  const showChangePasswordModal = (userId: number) => {
    selectedUserIdRef.current = userId;
    changePasswordModal();
    clearFirstNameValue();
    clearLastNameValue();
    clearEmailValue();
  };

  const columns = useGetUsersColumns({
    t,
    editUserHandler,
    showDeleteUserModal,
    showChangePasswordModal,
    changeSort,

    userNameValue,
    firstNameValue,
    lastNameValue,
    emailValue,
    rolesValue,
    superuserValue,

    handleUserNameValue,
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handleSuperuserChange,

    clearUserNameValue,
    clearFirstNameValue,
    clearLastNameValue,
    clearEmailValue,
    clearRolesValue,
    clearSuperuserValue,

    handleRolesChange,
  });

  const isVisibleClearFiltersButton =
    !!userNameValue ||
    !!firstNameValue ||
    !!lastNameValue ||
    !!emailValue ||
    !!rolesValue ||
    !!superuserValue;

  return (
    <>
      {contextHolder}

      {isVisibleClearFiltersButton && (
        <RenderClearFiltersButton onClick={clearAllFilters} />
      )}

      <Table
        columns={columns}
        dataSource={allUsers?.results}
        rowKey={(obj) => obj.id!}
        loading={isLoadingUsersFetching || isLoadingAllUsers}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: allUsers?.count,
        }}
      />
    </>
  );
};

export { UsersTable };
